import { useMemo } from "react";

import { FormProvider, useForm } from "react-hook-form";

import {
  useCreateConnection,
  useEditConnection,
} from "src/api/connectApi/queries";
import { Connection } from "src/api/connectApi/types";
import {
  taktileInternalPrefix,
  validateNotStartsWithInternalPrefix,
} from "src/api/constants";
import { Button } from "src/base-components/Button";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { MongoDBEnvironmentConfig } from "src/connections/config/database/mongodb/MongoDBEnvironmentConfig";
import {
  DatabasePermissionsPill,
  DatabaseType,
} from "src/connections/config/database/shared/DatabasePermissionsPill";
import { EnableNonProdConfigsField } from "src/connections/config/EnableNonProdConfigsField";
import { isFieldErrored } from "src/connections/config/isFieldErrored";
import { EnvironmentTabs } from "src/connections/config/shared/EnvironmentTabs";
import { errorProps } from "src/connections/config/shared/errorProps";
import {
  convertBEConnectionToMongoDBConnectionConfigInputs,
  convertMongoDBConnectionConfigInputsToBEConnection,
  getMongoDBConnectionConfigInputsDefaultValues,
} from "src/connections/model/mongodb";
import { MongoDBConnectionConfigInputs } from "src/connections/types";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";

type Props = {
  onClose: () => void;
  workspaceUrl: string;
  connection?: Connection;
};

export const MongoDBConnectionConfigForm: React.FC<Props> = ({
  onClose,
  workspaceUrl,
  connection,
}) => {
  const defaultValues = useMemo(() => {
    if (connection) {
      return convertBEConnectionToMongoDBConnectionConfigInputs(connection);
    } else {
      return getMongoDBConnectionConfigInputsDefaultValues();
    }
  }, [connection]);

  const formMethods = useForm<MongoDBConnectionConfigInputs>({
    defaultValues,
  });
  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    watch,
  } = formMethods;

  const enableNonProdConfigs = watch("enableNonProdConfigs");

  const createConnectionMutation = useCreateConnection(workspaceUrl);
  const editConnectionMutation = useEditConnection(
    workspaceUrl,
    connection?.id,
  );

  const onSubmit = async (data: MongoDBConnectionConfigInputs) => {
    const payload = convertMongoDBConnectionConfigInputsToBEConnection(data);
    try {
      if (connection) {
        await editConnectionMutation.mutateAsync({
          id: connection.id,
          payload,
        });
      } else {
        await createConnectionMutation.mutateAsync(payload);
      }

      const action = connection ? "updated" : "created";
      toastActions.success({
        title: `Connection ${action}`,
        description: `Connection ${data.name} has been ${action}.`,
      });
      onClose();
    } catch {
      toastActions.failure({
        title: "Error",
        description: "We couldn't save your connection",
      });
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Content>
          <FormItem
            description="A name that describes the new connection"
            gap="sm"
            label="Connection name"
            isRequired
          >
            {Boolean(errors.name) && (
              <ErrorHint>{errors.name?.message}</ErrorHint>
            )}
            <Input
              data-loc="mongodb-connection-name"
              placeholder="e.g. MongoDB Database"
              fullWidth
              {...errorProps(isFieldErrored(errors, "name"))}
              {...register("name", {
                required: "Connection name is required",
                validate: validateNotStartsWithInternalPrefix(
                  `Connection name cannot start with ${taktileInternalPrefix}`,
                ),
              })}
            />
          </FormItem>
          <EnableNonProdConfigsField<MongoDBConnectionConfigInputs>
            dataLoc="mongodb-connection-env-aware-switch"
            subtitle="You can set different credentials for test runs and sandbox DB queries"
            title="Use separate credentials for non-production DB queries"
          />
          {enableNonProdConfigs ? (
            <EnvironmentTabs
              prodContent={
                <MongoDBEnvironmentConfig
                  environment="production"
                  labelsPrefix="Production"
                />
              }
              prodError={Boolean(errors.productionConfig)}
              sandboxContent={
                <MongoDBEnvironmentConfig
                  environment="sandbox"
                  labelsPrefix="Sandbox"
                />
              }
              sandboxError={Boolean(errors.sandboxConfig)}
            />
          ) : (
            <MongoDBEnvironmentConfig
              environment="production"
              labelsPrefix=""
            />
          )}
          <DatabasePermissionsPill databaseType={DatabaseType.MongoDB} />
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              dataLoc="mongodb-connection-save"
              disabled={isSubmitting}
              htmlType="submit"
              loading={isSubmitting}
              size="base"
              variant="primary"
            >
              Save
            </Button>
          }
        ></Modal.Footer>
      </form>
    </FormProvider>
  );
};
